import { useState, useEffect, useRef } from "react";
import { MessageSquare, Send, Loader2 } from "lucide-react";
import ReactMarkdown from "react-markdown";

const WELCOME_MSG = {
  role: "assistant",
  text: "Hej! Spørg mig om astma, allergi, din dag eller noget praktisk.",
};

const SESSION_KEY = "ai-chat-history";

export default function AIChatWidget() {
  const [history, setHistory] = useState(() => {
    try {
      const cached = sessionStorage.getItem(SESSION_KEY);
      if (!cached) return [];
      const parsed = JSON.parse(cached);
      if (!Array.isArray(parsed)) return [];
      return parsed.filter((m) => m && m.text && m.text.trim() !== "");
    } catch {
      return [];
    }
  });

  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const historyFetchedRef = useRef(false);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || loading) return;

    const userMsg = { role: "user", text };

    setHistory((prev) => {
      const updated = [...prev, userMsg];
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(updated));
      return updated;
    });

    setInput("");
    setLoading(true);

    try {
      const token = localStorage.getItem("token");

      const res = await fetch("/api/chat", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({ message: text }),
      });

      const data = await res.json();

      const aiText =
        data?.content?.[0]?.text || data?.reply || "Jeg kunne ikke få et svar lige nu.";

      setHistory((prev) => {
        const updated = [...prev, { role: "assistant", text: aiText }];
        sessionStorage.setItem(SESSION_KEY, JSON.stringify(updated));
        return updated;
      });
    } catch {
      setHistory((prev) => {
        const updated = [
          ...prev,
          { role: "assistant", text: "Der opstod en fejl. Prøv igen." },
        ];
        sessionStorage.setItem(SESSION_KEY, JSON.stringify(updated));
        return updated;
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (historyFetchedRef.current) return;
    historyFetchedRef.current = true;

    if (history.length > 0) return;

    const fetchHistory = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token || token === "undefined" || token === "null") return;

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);

        const res = await fetch("/api/chat/history", {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          signal: controller.signal,
        });

        clearTimeout(timeoutId);

        if (res.ok) {
          const data = await res.json();
          if (Array.isArray(data) && data.length > 0) {
            const loaded = data
              .map((msg) => ({
                role: msg.role,
                text: msg.message || msg.text || "",
              }))
              .filter((m) => m.text && m.text.trim() !== "");

            setHistory(loaded);
            sessionStorage.setItem(SESSION_KEY, JSON.stringify(loaded));
          }
        }
      } catch {
        // ignore
      }
    };

    fetchHistory();
  }, [history.length]);

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const messages = [WELCOME_MSG, ...history];

  return (
    <div
      style={{
        border: "1px solid var(--border-light)",
        borderRadius: 16,
        padding: 16,
        background: "var(--bg-white)",
        display: "flex",
        flexDirection: "column",
        gap: 12,
        transition: "background-color 200ms ease, border-color 200ms ease",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, color: "var(--text-primary)" }}>
        <MessageSquare size={18} />
        <strong>AI chat</strong>
      </div>

      <div
        style={{
          maxHeight: 260,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          gap: 10,
          paddingRight: 4,
        }}
      >
        {messages.map((m, i) => (
          <div
            key={i}
            style={{
              alignSelf: m.role === "user" ? "flex-end" : "flex-start",
              background: m.role === "user" ? "var(--chat-user-bg)" : "var(--chat-assistant-bg)",
              color: m.role === "user" ? "var(--chat-user-text)" : "var(--chat-assistant-text)",
              padding: "10px 12px",
              borderRadius: 14,
              maxWidth: "85%",
              transition: "background-color 200ms ease, color 200ms ease",
              whiteSpace: "pre-wrap",
            }}
          >
            {m.role === "assistant" ? (
              <ReactMarkdown
                components={{
                  p: ({ node, ...props }) => (
                    <p style={{ margin: "0 0 8px 0", ...props.style }} {...props} />
                  ),
                  strong: ({ node, ...props }) => <strong style={{ fontWeight: "bold" }} {...props} />,
                  em: ({ node, ...props }) => <em style={{ fontStyle: "italic" }} {...props} />,
                  ul: ({ node, ...props }) => (
                    <ul style={{ marginLeft: "20px", marginTop: "8px", marginBottom: "8px" }} {...props} />
                  ),
                  li: ({ node, ...props }) => <li style={{ marginBottom: "4px" }} {...props} />,
                  code: ({ node, inline, ...props }) => (
                    <code
                      style={{
                        background: "var(--chat-code-bg)",
                        padding: inline ? "2px 6px" : "10px",
                        borderRadius: "4px",
                        fontFamily: "monospace",
                        fontSize: "0.9em",
                      }}
                      {...props}
                    />
                  ),
                  blockquote: ({ node, ...props }) => (
                    <blockquote
                      style={{
                        borderLeft: "3px solid var(--border-light)",
                        paddingLeft: "10px",
                        marginLeft: "0",
                        opacity: "0.8",
                      }}
                      {...props}
                    />
                  ),
                }}
              >
                {m.text}
              </ReactMarkdown>
            ) : (
              m.text
            )}
          </div>
        ))}

        {loading && (
          <div
            style={{
              alignSelf: "flex-start",
              display: "flex",
              alignItems: "center",
              gap: 8,
              color: "var(--text-secondary)",
            }}
          >
            <Loader2 size={16} className="animate-spin" />
            Skriver svar...
          </div>
        )}
      </div>

      <div style={{ display: "flex", gap: 8 }}>
        <textarea
          rows={2}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Skriv en besked..."
          style={{
            flex: 1,
            resize: "none",
            borderRadius: 12,
            border: "1px solid var(--border-light)",
            padding: 10,
            outline: "none",
            background: "var(--bg-white)",
            color: "var(--text-primary)",
            transition: "background-color 200ms ease, color 200ms ease, border-color 200ms ease",
          }}
        />

        <button
          onClick={sendMessage}
          disabled={loading || !input.trim()}
          style={{
            border: "none",
            borderRadius: 12,
            padding: "0 14px",
            background: loading || !input.trim() ? "var(--text-secondary)" : "var(--text-primary)",
            color: "white",
            cursor: loading || !input.trim() ? "not-allowed" : "pointer",
            transition: "background-color 200ms ease",
          }}
          title="Send"
        >
          {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
        </button>
      </div>
    </div>
  );
}